import { useCallback, useMemo, useState } from 'react';
import { ExecutionResult } from 'graphql';
import { GraphQLData } from '../types/graphql';
import { showError, showSuccess } from '../utils/toast';

const DEFAULT_ERROR = 'Update failed';

/**
 * Returns `mutateAndSetState` fn, which:
 * - runs `mutate()` and displays errors if any occur
 * - does not re-throw errors
 * - manages `loading` and `errors` state
 * - displays `successMessage` upon success (if provided)
 */
const useMutateWithState = () => {
  const [loading, setLoading] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  const mutateAndSetState = async <T>(
    mutate: () => Promise<ExecutionResult<GraphQLData<T>>>,
    successMessage?: string | ((result: T) => string),
  ) => {
    setLoading(true);
    try {
      const response = await mutate();
      const { data, errors: topLevelErrors } = response;
      // Sometimes, data exists but is null or has a key that is null
      // Generally, graphql will throw an error in this case
      // unless you passed an onError function to the mutate
      const { result, errors } = Object.values(data || {})[0] || {};

      // success
      if (result) {
        if (successMessage) {
          const msg =
            typeof successMessage === 'function'
              ? successMessage(result)
              : successMessage;
          showSuccess(msg);
        }
        return response;
      }

      // errors
      setHasErrors(true);
      if (Array.isArray(topLevelErrors) && topLevelErrors?.length) {
        // top-level errors
        showError(topLevelErrors[0]);
      } else if (topLevelErrors instanceof Error) {
        showError(topLevelErrors);
      } else {
        // validation errors
        showError(errors || [DEFAULT_ERROR]);
      }
      return undefined;
    } catch (err) {
      showError(err);
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  const mutationState = useMemo(() => ({ loading, hasErrors }), [
    loading,
    hasErrors,
  ]);

  const resetState = useCallback(() => {
    setLoading(false);
    setHasErrors(false);
  }, []);

  return [mutationState, mutateAndSetState, resetState] as const;
};

export default useMutateWithState;

export type MutateAndSetStateType = <T>(
  mutate: () => Promise<ExecutionResult<GraphQLData<T>>>,
  successMessage?: string | ((result: T) => string),
) => Promise<Nullable<ExecutionResult<GraphQLData<T>>>>;
